import {
  Address,
  BirthPlace,
  DateType,
  EntityProfile,
  EReputationMatchResult,
  GroupedCountryDetail,
  GroupedDate,
  GroupedName,
  GroupedRole,
  Identifier,
  Industry,
  NameType,
  PersonProfile,
  Relation,
  WatchlistFlag,
} from "src/core/types/entity";
import { WatchlistIconType } from "src/core/types/watchlist";

type SearchResultsResponse = {
  data: {
    id: string;
    attributes: {
      primary_name: string;
      score: string;
      country_territory_code: string;
      country_territory_name: string;
      date_of_birth: { day?: string; month?: string; year?: string }[];
      icon_hints: { icon_hint: string; status: string }[];
    };
  }[];
};

type RawOriginalScriptName = { name: string; language?: string };

type RawEntityNameEntry = {
  name?: string;
  suffix?: string;
  associated_names?: { original_script_names?: RawOriginalScriptName[] };
};

type RawEntityNameDetails = {
  primary_name?: RawEntityNameEntry;
  also_known_as?: RawEntityNameEntry[];
  formerly_known_as?: RawEntityNameEntry[];
};

type RawIdentifierDetail = {
  type: string;
  value: string;
  document_type?: string;
  comments?: string;
};

type RawIndustryDetail = {
  code: string;
  descriptor: string;
  description?: string;
};

export const parseRawMatchResultsEntityJson = (
  rawSearchResults: string,
  detailedResults: { content: string; positionInSearchResults: number }[]
): EReputationMatchResult[] => {
  const parsed: { data: SearchResultsResponse["data"] } = JSON.parse(rawSearchResults);

  return parsed.data.map((entry) => {
    const { id, attributes } = entry;

    const { primary_name, score, country_territory_code, country_territory_name, icon_hints } =
      attributes;

    // Risk icons filtrées sur SAN-ENTITY ou SIE
    const riskIcons = (icon_hints ?? [])
      .map((hint) => hint.icon_hint)
      .filter((icon) => icon === WatchlistIconType.SAN_ENTITY || icon === WatchlistIconType.SIE);

    // Récupération du detailed result correspondant
    const matchingDetailedResult = detailedResults.find((detailedResult) => {
      const parsedDetail = JSON.parse(detailedResult.content);
      const entityId = parsedDetail?.data?.id;

      return entityId === id;
    });

    const entityProfile = matchingDetailedResult
      ? mapJsonToEntityProfile(matchingDetailedResult.content)
      : getEmptyEntityProfile();
    const numericScore = Number.isFinite(Number(score)) ? Number(score) : 0;

    return {
      id,
      score: Math.round(numericScore * 100),
      primaryName: primary_name,
      countryCode: country_territory_code,
      countryName: country_territory_name,
      riskIcons,
      detail: entityProfile,
    };
  });
};

export function mapJsonToEntityProfile(json: string): EntityProfile {
  const parsed = JSON.parse(json);
  const data = parsed.data;
  const basic = data?.attributes?.basic;
  const entity = data?.attributes?.entity;
  const watchlist = data?.attributes?.watchlist;
  const adverseMedia = data?.attributes?.adverse_media_entity;

  return {
    names: extractEntityNamesFromJson(basic?.name_details),
    registrationDates: extractGroupedDatesFromJson(
      entity?.date_details?.registration,
      "REGISTRATION"
    ),
    cessationDates: extractGroupedDatesFromJson(entity?.date_details?.cessation, "CESSATION"),
    countriesOfRegistration: extractCountryDetailsFromJson(
      entity?.country_territory_details?.registration
    ),
    industries: extractIndustriesFromJson(entity?.industries),
    identifiers: extractIdentifiersFromJson(entity?.identification_details),
    addresses: extractAddressesFromJson(watchlist?.address_details),
    watchlist: extractWatchlistFlagsFromJson(watchlist?.content_type_details?.content_types),
    sources: extractSourcesFromJson(adverseMedia?.sources ?? watchlist?.sources),
    notes: extractNotesFromJson(watchlist?.comment_details),
    relations: extractRelationsFromJson(data?.attributes?.relationship?.connection_details),
  };
}

export const getEmptyEntityProfile = (): EntityProfile => {
  return {
    names: [],
    registrationDates: [],
    cessationDates: [],
    countriesOfRegistration: [],
    industries: [],
    identifiers: [],
    addresses: [],
    watchlist: [],
    sources: [],
    notes: [],
    relations: [],
  };
};

export const parseRawMatchResultsJson = (
  rawSearchResults: string,
  detailedResults: { content: string; positionInSearchResults: number }[]
): EReputationMatchResult[] => {
  const parsed: { data: SearchResultsResponse["data"] } = JSON.parse(rawSearchResults);

  return parsed.data.map((entry) => {
    const { id, attributes } = entry;
    const {
      primary_name,
      score,
      country_territory_code,
      country_territory_name,
      date_of_birth,
      icon_hints,
    } = attributes;

    // Date de naissance : format la plus complète
    let dateOfBirth = "";

    for (const dob of date_of_birth ?? []) {
      const { day, month, year } = dob;
      if (year && month && day) {
        dateOfBirth = `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
        break;
      } else if (year && month) {
        dateOfBirth ||= `${month.padStart(2, "0")}/${year}`;
      } else if (year) {
        dateOfBirth ||= year;
      }
    }

    // Risk icons filtrées sur SAN-PERSON ou PEP
    const riskIcons = (icon_hints ?? [])
      .map((hint) => hint.icon_hint)
      .filter((icon) => icon === WatchlistIconType.SAN_PERSON || icon === WatchlistIconType.PEP);

    const matchingDetailedResult = detailedResults.find((detailedResult) => {
      const parsed = JSON.parse(detailedResult.content);
      const personId = parsed?.data?.id;

      return personId === id;
    });

    const personProfile = matchingDetailedResult
      ? mapJsonToPersonProfile(matchingDetailedResult.content)
      : getEmptyPersonProfile(); // Fallback si pas de détails trouvés

    const numericScore = Number.isFinite(Number(score)) ? Number(score) : 0;

    return {
      id,
      score: Math.round(numericScore * 100),
      primaryName: primary_name,
      countryCode: country_territory_code,
      countryName: country_territory_name,
      dateOfBirth,
      riskIcons,
      detail: personProfile,
    };
  });
};

export const getEmptyPersonProfile = (): PersonProfile => {
  return {
    gender: null,
    names: [],
    birthDates: [],
    birthPlaces: [],
    countryDetails: [],
    addresses: [],
    images: [],
    watchlist: [],
    roles: [],
    sources: [],
    notes: [],
    relations: [],
  };
};

export function mapJsonToPersonProfile(json: string): PersonProfile {
  const parsed = JSON.parse(json);
  const data = parsed.data;
  const basic = data?.attributes?.basic;
  const person = data?.attributes?.person;
  const watchlist = data?.attributes?.watchlist;
  const imageDetails = data?.attributes?.image?.image_details || [];

  return {
    gender: person?.gender ?? null,
    names: extractNamesFromJson(basic?.name_details),
    birthDates: extractGroupedDatesFromJson(person?.date_details?.birth, "BIRTH"),
    birthPlaces: extractBirthPlacesFromJson(person?.places_of_birth),
    countryDetails: extractCountryDetailsFromJson(person?.country_territory_details),
    addresses: extractAddressesFromJson(basic?.address_details),
    images: imageDetails.map((img: { external_url: string }) => img.external_url).filter(Boolean),
    watchlist: extractWatchlistFlagsFromJson(watchlist?.content_type_details?.content_types),
    roles: extractRolesFromJson(watchlist?.role_details),
    sources: extractSourcesFromJson(watchlist?.sources),
    notes: extractNotesFromJson(watchlist?.comment_details),
    relations: extractRelationsFromJson(data?.attributes?.relationship?.connection_details),
  };
}

const extractEntityNamesFromJson = (
  nameDetails: RawEntityNameDetails | null | undefined
): GroupedName[] => {
  if (!nameDetails) return [];

  const result: GroupedName[] = [];

  const buildFullName = (name?: string, suffix?: string) =>
    [name, suffix].filter(Boolean).join(" ");

  const mapGroup = (type: NameType, entries: RawEntityNameEntry[]) => {
    return {
      type,
      names: entries.map((entry) => ({
        fullName: buildFullName(entry?.name, entry?.suffix),
        originalScripts:
          entry?.associated_names?.original_script_names?.map((os: RawOriginalScriptName) => ({
            name: os.name,
            langCode: os.language,
          })) ?? [],
      })),
    };
  };

  // Primary Name
  if (nameDetails?.primary_name) {
    result.push(mapGroup("Primary Name", [nameDetails.primary_name]));
  }

  // Also Known As
  if (Array.isArray(nameDetails?.also_known_as) && nameDetails.also_known_as.length) {
    result.push(mapGroup("Also Known As", nameDetails.also_known_as));
  }

  // Formerly Known As
  if (Array.isArray(nameDetails?.formerly_known_as) && nameDetails.formerly_known_as.length) {
    result.push(mapGroup("Formerly Known As" as NameType, nameDetails.formerly_known_as));
  }

  return result;
};

const extractNamesFromJson = (nameDetails: {
  primary_name: {
    first_name: string;
    middle_name: string;
    surname: string;
    associated_names: { original_script_names: { name: string; language: string }[] };
  };
  also_known_as: {
    first_name: string;
    middle_name: string;
    surname: string;
    associated_names: { original_script_names: { name: string; language: string }[] };
  }[];
  expanded_language_variation: {
    first_name: string;
    middle_name: string;
    surname: string;
    associated_names: { original_script_names: { name: string; language: string }[] };
  }[];
}): GroupedName[] => {
  const result: GroupedName[] = [];

  const group = (
    type: string,
    names: {
      first_name: string;
      middle_name: string;
      surname: string;
      associated_names: { original_script_names: { name: string; language: string }[] };
    }[]
  ) => {
    return {
      type: type as GroupedName["type"],
      names: names.map((n) => ({
        firstName: n.first_name,
        middleName: n.middle_name,
        surname: n.surname,
        originalScripts:
          n.associated_names?.original_script_names?.map(
            (os: { name: string; language: string }) => ({
              name: os.name,
              langCode: os.language,
            })
          ) || [],
      })),
    };
  };

  if (nameDetails?.primary_name) result.push(group("Primary Name", [nameDetails.primary_name]));
  if (nameDetails?.also_known_as) result.push(group("Also Known As", nameDetails.also_known_as));
  if (nameDetails?.expanded_language_variation)
    result.push(group("Expanded Language Variation", nameDetails.expanded_language_variation));

  return result;
};

const extractBirthPlacesFromJson = (
  places: {
    city: string;
    country: { descriptor: string; code: string; iso_alpha3: string };
  }[]
): BirthPlace[] => {
  if (!Array.isArray(places)) return [];
  return places.map((place) => ({
    place: place.city,
    country: place.country?.descriptor || place.country?.code || "",
  }));
};

const extractCountryDetailsFromJson = (details: {
  citizenship: { descriptor: string; code: string; iso_alpha3: string }[];
  residence: { descriptor: string; code: string; iso_alpha3: string }[];
  jurisdiction: { descriptor: string; code: string; iso_alpha3: string }[];
}): GroupedCountryDetail[] => {
  if (!details) return [];

  const result: GroupedCountryDetail[] = [];
  for (const key of ["citizenship", "residence", "jurisdiction"]) {
    const countries = details?.[key as keyof typeof details];
    if (Array.isArray(countries) && countries.length) {
      result.push({
        type: key,
        countries: countries
          .map((c) => c.descriptor || c.code || c.iso_alpha3 || "")
          .filter(Boolean),
      });
    }
  }
  return result;
};

const extractAddressesFromJson = (
  addresses: {
    address: string;
    city: string;
    region: string;
    country: { descriptor: string; code: string; iso_alpha3: string };
  }[]
): Address[] => {
  if (!Array.isArray(addresses)) return [];
  return addresses.map((addr) => ({
    addressLine: addr.address,
    city: addr.city,
    region: addr.region,
    country: addr.country?.descriptor || addr.country?.code || "",
    iso3: addr.country?.iso_alpha3,
  }));
};

const extractWatchlistFlagsFromJson = (
  contentTypes: {
    description: string;
    active_status: string;
    sub_content_types?: { description: string; active_status: string }[];
  }[]
): WatchlistFlag[] => {
  if (!Array.isArray(contentTypes)) return [];

  return contentTypes.flatMap((ct) => {
    const mainStatus = ct.active_status ? ` (${ct.active_status})` : "";
    const mainDesc = ct.description + mainStatus;

    if (ct.sub_content_types && ct.sub_content_types.length > 0) {
      return ct.sub_content_types.map((sub) => {
        const subStatus = sub.active_status ? ` (${sub.active_status})` : "";
        return {
          type: ct.description,
          description: `${ct.description} > ${sub.description}${subStatus}`,
        };
      });
    }

    return [
      {
        type: ct.description,
        description: mainDesc,
      },
    ];
  });
};

const extractRolesFromJson = (rolesData: {
  primary: {
    category_type: string;
    title: string;
    since: { day: number; month: number; year: number };
    to: { day: number; month: number; year: number };
  };
  other: {
    category_type: string;
    title: string;
    since: { day: number; month: number; year: number };
    to: { day: number; month: number; year: number };
  }[];
  previous: {
    category_type: string;
    title: string;
    since: { day: number; month: number; year: number };
    to: { day: number; month: number; year: number };
  }[];
}): GroupedRole[] => {
  if (!rolesData) return [];

  const mapRole = (
    roleArray: {
      category_type: string;
      title: string;
      since: { day: number; month: number; year: number };
      to: { day: number; month: number; year: number };
    }[],
    type: string
  ): GroupedRole => ({
    type,
    roles: roleArray.map((r) => ({
      occupationCategory: r.category_type,
      occupation: r.title,
      startDate: r.since ? formatDate(r.since) : "",
      endDate: r.to ? formatDate(r.to) : "",
    })),
  });

  const groupedRoles: GroupedRole[] = [];
  if (rolesData?.primary) groupedRoles.push(mapRole([rolesData.primary], "Primary Occupation"));
  if (rolesData?.other) groupedRoles.push(mapRole(rolesData.other, "Other Roles"));
  if (rolesData?.previous) groupedRoles.push(mapRole(rolesData.previous, "Previous Roles"));

  return groupedRoles;
};

const extractSourcesFromJson = (
  sources?: { external_link?: string; title?: string }[]
): string[] => {
  if (!Array.isArray(sources)) return [];
  return sources.map((s) => s.external_link ?? s.title).filter((v): v is string => Boolean(v));
};

const extractNotesFromJson = (comments: { list_comment?: string }): string[] => {
  if (comments?.list_comment) {
    return [comments.list_comment];
  }

  return [];
};

const extractRelationsFromJson = (
  connections: {
    profile_id: string;
    name_detail: { full_name: string; name: string };
    connection_type: string;
    icon_hints: { icon_hint: string; status: string }[];
  }[]
): Relation[] => {
  if (!Array.isArray(connections)) return [];
  return connections.map((conn) => ({
    peid: String(conn.profile_id),
    name: conn.name_detail?.full_name || conn.name_detail?.name || "",
    relationship: conn.connection_type,
    watchlist: (conn.icon_hints || []).map((h: { icon_hint: string; status: string }) => ({
      type: h.icon_hint,
      description: h.status,
    })),
  }));
};

const formatDate = (date: { day?: number; month?: number; year: number }): string => {
  if (!date?.year) return "";
  const d = date.day ? String(date.day).padStart(2, "0") : undefined;
  const m = date.month ? String(date.month).padStart(2, "0") : undefined;
  return d && m ? `${date.year}-${m}-${d}` : m ? `${date.year}-${m}` : `${date.year}`;
};

const extractIdentifiersFromJson = (details: RawIdentifierDetail[] = []): Identifier[] => {
  return details.map((item) => ({
    type: item.type,
    value: item.value,
    label: item.document_type ?? undefined,
    comments: item.comments ?? undefined,
  }));
};

const extractIndustriesFromJson = (industries: RawIndustryDetail[] = []): Industry[] => {
  return industries.map((industry) => ({
    code: industry.code,
    label: industry.descriptor,
    description: industry.description ?? undefined,
  }));
};

const formatDowJonesDate = (date?: {
  day?: number | string;
  month?: number | string;
  year?: number | string;
}): string | null => {
  if (!date?.year) return null;

  const year = String(date.year);
  const month = date.month ? String(date.month).padStart(2, "0") : null;
  const day = date.day ? String(date.day).padStart(2, "0") : null;

  if (day && month) return `${day}/${month}/${year}`;
  if (month) return `${month}/${year}`;
  return year;
};

const extractGroupedDatesFromJson = (
  rawDates: { date: { day?: number; month?: number; year?: number } }[] = [],
  type: DateType
): GroupedDate[] => {
  if (!Array.isArray(rawDates) || !rawDates.length) return [];

  const formatted = rawDates
    .map((item) => formatDowJonesDate(item?.date))
    .filter(Boolean) as string[];

  if (!formatted.length) return [];

  return [
    {
      type,
      dates: formatted,
    },
  ];
};
